import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaExternalLinkAlt } from "react-icons/fa";

import BlackHatLogo from "../../assets/images/blackhat.png";
import rsacLogo from "../../assets/images/rsac.png";
import owaspLogo from "../../assets/images/owasp.png";
import defconLogo from "../../assets/images/defcon.jpeg";
import gartnerLogo from "../../assets/images/gartner.png";
import bsidesLogo from "../../assets/images/bsides.jpeg";
import cloudexpoLogo from "../../assets/images/cloudexpo.jpeg";
import infosceLogo from "../../assets/images/infosec.jpeg";
import cybersecsumLogo from "../../assets/images/cybersecsum.jpeg";
import europesecLogo from "../../assets/images/europesec.png";

const talks = [
  {
    date: "November 2025",
    event: "OWASP Global AppSec DC",
    location: "Washington, D.C., USA",
    title: "OWASP VXDF: Is that really vulnerable? Show me the VXDF!",
    abstract:
      "Presentation on OWASP VXDF and practical demonstration of vulnerability assessment workflows. Slides and recording available via the event page.",
    logo: owaspLogo,
    gradient: "from-indigo-400 to-indigo-600",
    link: "https://owaspglobalappsecusa2025.sched.com/event/29Ilj",
  },
  {
    date: "August 2023",
    event: "Black Hat USA",
    location: "Las Vegas, NV, USA",
    title: "Securing Cloud Infrastructure at Scale",
    abstract:
      "Discussing advanced methodologies for securing multi-cloud infrastructure, covering zero trust principles and the latest in cloud workload protection.",
    logo: BlackHatLogo,
    gradient: "from-gray-600 to-gray-800",
  },
  {
    date: "March 2023",
    event: "RSA Conference",
    location: "San Francisco, CA, USA",
    title: "Enhancing Cloud Security with DevSecOps",
    abstract:
      "Exploring DevSecOps integrations for the cloud, focusing on automated security checks and compliance in CI/CD pipelines for faster, safer deployments.",
    logo: rsacLogo,
    gradient: "from-blue-400 to-blue-600",
  },
  {
    date: "November 2022",
    event: "OWASP Global AppSec",
    location: "London, UK",
    title: "AppSec in the Age of Containers and Microservices",
    abstract:
      "A comprehensive session on adapting application security practices to containerized and microservices architectures, including secure coding and vulnerability management.",
    logo: owaspLogo,
    gradient: "from-purple-400 to-purple-600",
  },
  {
    date: "September 2021",
    event: "DEF CON",
    location: "Las Vegas, NV, USA",
    title: "Container Security in Cloud Native Environments",
    abstract:
      "Addressing security risks specific to containerized applications in cloud-native environments, focusing on runtime protection, image scanning, and container hardening.",
    logo: defconLogo,
    gradient: "from-red-400 to-red-600",
  },
  {
    date: "June 2021",
    event: "Gartner Security & Risk Management Summit",
    location: "Sydney, Australia",
    title: "Managing Risk in Cloud Migration",
    abstract:
      "A strategic look at managing risk and maintaining security posture during cloud migration, with insights into regulatory compliance and risk assessment frameworks.",
    logo: gartnerLogo,
    gradient: "from-cyan-400 to-cyan-600",
  },
  {
    date: "October 2020",
    event: "BSides Security",
    location: "Amsterdam, Netherlands",
    title: "Adapting Application Security for Serverless Architectures",
    abstract:
      "Discussing the unique security challenges in serverless architectures, including attack surface reduction and best practices for securing serverless functions.",
    logo: bsidesLogo,
    gradient: "from-yellow-400 to-yellow-600",
  },
  {
    date: "February 2019",
    event: "Cloud Security Expo",
    location: "London, UK",
    title: "Zero Trust Model for Cloud Security",
    abstract:
      "An in-depth exploration of the Zero Trust model in cloud security, focusing on identity and access management, micro-segmentation, and continuous authentication.",
    logo: cloudexpoLogo,
    gradient: "from-green-400 to-green-600",
  },
  {
    date: "May 2018",
    event: "InfoSec World",
    location: "Orlando, FL, USA",
    title: "Securing APIs in Cloud-Based Applications",
    abstract:
      "Covering the essentials of securing APIs for cloud applications, with discussions on authentication, authorization, and common API vulnerabilities.",
    logo: infosceLogo,
    gradient: "from-orange-400 to-orange-600",
  },
  {
    date: "July 2017",
    event: "AppSec USA by OWASP",
    location: "Washington, D.C., USA",
    title: "Integrating Application Security into CI/CD Pipelines",
    abstract:
      "Insights into embedding security into CI/CD workflows, reducing vulnerabilities early in development through automated testing and security tooling.",
    logo: owaspLogo,
    gradient: "from-indigo-400 to-indigo-600",
  },
  {
    date: "March 2016",
    event: "Cyber Security Summit",
    location: "New York, NY, USA",
    title: "Advanced Threat Protection for Cloud Applications",
    abstract:
      "Exploring the latest tools and techniques for protecting cloud applications against advanced threats, including AI-driven threat detection and response strategies.",
    logo: cybersecsumLogo,
    gradient: "from-pink-400 to-pink-600",
  },
  {
    date: "December 2015",
    event: "European Security Summit",
    location: "Berlin, Germany",
    title: "Ensuring Data Privacy in the Cloud",
    abstract:
      "Focusing on compliance and privacy issues in cloud environments, including data encryption, GDPR compliance, and secure data management practices.",
    logo: europesecLogo,
    gradient: "from-teal-400 to-teal-600",
  },
];

const TalksTimeline = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);

  const handleCardClick = (talk) => {
    setSelectedTalk(talk);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedTalk(null);
  };

  return (
    <section className="py-12 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-3xl sm:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Talks Timeline
        </motion.h2>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-400 via-purple-400 to-pink-400" />

          <div className="space-y-10 relative z-10">
            {talks.map((talk, index) => (
              <motion.div
                key={index}
                className={`relative max-w-lg ${
                  index % 2 === 0 ? "ml-auto pr-8" : "mr-auto pl-8"
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <motion.div
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl shadow-xl p-6 cursor-pointer hover:bg-white/10 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => handleCardClick(talk)}
                >
                  <div className="flex items-center mb-4">
                    {talk.logo && (
                      <div className="bg-white/10 p-2 rounded-lg mr-4">
                        <img
                          src={talk.logo}
                          alt={`${talk.event} logo`}
                          className="w-8 h-8"
                        />
                      </div>
                    )}
                    <h3
                      className={`text-xl font-semibold bg-gradient-to-r ${talk.gradient} bg-clip-text text-transparent`}
                    >
                      {talk.date}
                    </h3>
                  </div>
                  <p className="text-lg font-bold text-white mb-1">
                    {talk.event}
                  </p>
                  <p className="text-md text-gray-300 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-blue-400" />
                    {talk.location}
                  </p>
                  <p className="text-gray-400 mt-3 italic">{talk.title}</p>
                </motion.div>

                {/* Timeline dot */}
                <motion.div
                  className={`absolute top-1/2 transform -translate-y-1/2 ${
                    index % 2 === 0 ? "-left-3" : "-right-3"
                  } w-6 h-6 bg-gradient-to-r ${talk.gradient} rounded-full border-4 border-gray-800`}
                  whileHover={{ scale: 1.3 }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Custom Modal */}
        <AnimatePresence>
          {openDialog && selectedTalk && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={handleClose}
              />

              {/* Modal Content */}
              <motion.div
                className="relative bg-gray-800/90 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full p-8"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25 }}
              >
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes className="text-xl" />
                </button>

                <div className="flex items-center mb-4">
                  {selectedTalk.logo && (
                    <div className="bg-white/10 p-3 rounded-lg mr-4">
                      <img
                        src={selectedTalk.logo}
                        alt={`${selectedTalk.event} logo`}
                        className="w-12 h-12"
                      />
                    </div>
                  )}
                  <div>
                    <h2
                      className={`text-2xl font-bold bg-gradient-to-r ${selectedTalk.gradient} bg-clip-text text-transparent`}
                    >
                      {selectedTalk.title}
                    </h2>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <p className="flex items-center text-gray-300">
                    <FaCalendarAlt className="mr-3 text-blue-400" />
                    <strong className="mr-2">Date:</strong> {selectedTalk.date}
                  </p>
                  <p className="text-gray-300">
                    <strong>Event:</strong> {selectedTalk.event}
                  </p>
                  <p className="flex items-center text-gray-300">
                    <FaMapMarkerAlt className="mr-3 text-blue-400" />
                    <strong className="mr-2">Location:</strong>{" "}
                    {selectedTalk.location}
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Abstract:</strong>{" "}
                    {selectedTalk.abstract}
                  </p>

                  {/* External link / resources */}
                  {selectedTalk.link && (
                    <div className="mt-6">
                      <a
                        href={selectedTalk.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-vxdf-accent text-white rounded-full font-semibold hover:scale-105 transition-all duration-150"
                      >
                        View on Sched <FaExternalLinkAlt />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TalksTimeline;
